import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Ruler } from 'lucide-react';
import { Link } from 'react-router-dom';

import usePageMetadata from '../../hooks/usePageMetadata';
import { getLimitDeviation } from '../../services/toleranceService';
import {
  formatDesignation,
  formatDeviation,
  formatDimension,
  getResultPrecision,
  parseToleranceInput,
  setToleranceInputType,
} from './toleranceDisplay';
import './LimitDeviationsPage.css';
import { useUnits } from '../../units/UnitContext';
import { formatUnitValue, unitLabel } from '../../units/unitConversions';

const REQUEST_DELAY_MS = 250;
const PAGE_TITLE = 'ISO 286 Limit Deviations Calculator | ASiJoLab';
const PAGE_DESCRIPTION =
  'Calculate ISO 286 limit deviations for holes and shafts from 1 to 500 mm. Enter a tolerance such as 25H7 and get upper and lower dimensional limits instantly.';

const ToleranceField = ({ lowerUm, upperUm, unitSystem }) => {
  const minimum = Math.min(lowerUm, 0);
  const maximum = Math.max(upperUm, 0);
  const span = maximum - minimum || 1;
  const toTop = (value) => ((maximum - value) / span) * 100;
  const fieldTop = toTop(upperUm);
  const fieldBottom = toTop(lowerUm);

  return (
    <div className="limitToleranceField" aria-label="Tolerance field relative to zero">
      <span className="limitToleranceUpper">{formatDeviation(upperUm, unitSystem)}</span>
      <div className="limitTolerancePlot" aria-hidden="true">
        <span
          className="limitToleranceZero"
          style={{ top: `${toTop(0)}%` }}
        />
        <span
          className="limitToleranceBand"
          style={{
            top: `${fieldTop}%`,
            height: `${Math.max(fieldBottom - fieldTop, 3)}%`,
          }}
        />
      </div>
      <span className="limitToleranceLower">{formatDeviation(lowerUm, unitSystem)}</span>
      <span className="limitToleranceZeroLabel">Zero line</span>
    </div>
  );
};

const CompactTolerance = ({ result, label, selected = false, unitSystem }) => {
  if (!result) return null;

  const precision = getResultPrecision(result);

  return (
    <li className={selected ? 'limitAdjacentItem isSelected' : 'limitAdjacentItem'}>
      <div>
        <strong>{result.position}{result.grade}</strong>
        <span>{label}</span>
      </div>
      <p>
        {formatDimension(result.limits.minimumMm, precision, unitSystem)} –{' '}
        {formatDimension(result.limits.maximumMm, precision, unitSystem)} {unitLabel('mm', unitSystem)}
      </p>
      <small>
        {formatDeviation(result.deviations.lowerUm, unitSystem)} /{' '}
        {formatDeviation(result.deviations.upperUm, unitSystem)}
      </small>
    </li>
  );
};

const LimitDeviationsPage = () => {
  const { unitSystem } = useUnits();
  const [inputValue, setInputValue] = useState('');
  const [selectedType, setSelectedType] = useState('hole');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [adjacentOpen, setAdjacentOpen] = useState(false);
  const parsedInput = useMemo(
    () => parseToleranceInput(inputValue),
    [inputValue]
  );

  usePageMetadata(PAGE_TITLE, PAGE_DESCRIPTION);

  useEffect(() => {
    if (parsedInput.status === 'valid') {
      setSelectedType(parsedInput.type);
    }
  }, [parsedInput]);

  useEffect(() => {
    if (parsedInput.status !== 'valid') {
      setLoading(false);
      setResult(null);
      setMessage(
        parsedInput.status === 'invalid'
          ? { type: 'invalid', text: parsedInput.message }
          : null
      );
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setMessage(null);

    const timer = window.setTimeout(async () => {
      try {
        const loadedResult = await getLimitDeviation({
          system: 'ISO286',
          type: parsedInput.type,
          position: parsedInput.position,
          grade: parsedInput.grade,
          diameter: parsedInput.diameter,
          includeAdjacent: true,
        }, { signal: controller.signal });

        if (!active) return;
        setResult(loadedResult);
        setAdjacentOpen(false);
      } catch (error) {
        if (!active || error?.code === 'ERR_CANCELED') return;

        setResult(null);
        if (
          error?.response?.status === 404 &&
          error?.response?.data?.code === 'TOLERANCE_NOT_AVAILABLE'
        ) {
          setMessage({
            type: 'notAvailable',
            text: 'Tolerance not available for this size.',
          });
        } else if (error?.response?.status === 400) {
          setMessage({
            type: 'invalid',
            text: error?.response?.data?.message || 'Check the tolerance designation.',
          });
        } else {
          setMessage({
            type: 'error',
            text: 'The tolerance could not be loaded. Please try again.',
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    }, REQUEST_DELAY_MS);

    return () => {
      active = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [parsedInput]);

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setInputValue((currentValue) => setToleranceInputType(currentValue, type));
  };

  const precision = result ? getResultPrecision(result) : 3;
  const adjacentResults = result?.adjacent;
  const hasAdjacentResults = Boolean(
    adjacentResults?.lowerGrade || adjacentResults?.higherGrade
  );
  const fitsPath = result
    ? `/tolerances/fits?${new URLSearchParams({
        diameter: String(result.nominalSizeMm),
        [result.type]: `${result.position}${result.grade}`,
      }).toString()}`
    : null;
  const supportsGauge = Boolean(
    result &&
    result.grade <= 16 &&
    result.grade >= (result.type === 'hole' ? 6 : 5)
  );
  const gaugePath = supportsGauge
    ? `/tolerances/gauges?${new URLSearchParams({
        diameter: String(result.nominalSizeMm),
        tolerance: `${result.position}${result.grade}`,
      }).toString()}`
    : null;

  return (
    <main className="limitDeviationsPage">
      <header className="limitDeviationsHeader">
        <p>Tolerances · ISO 286</p>
        <h1>Limit Deviations</h1>
        <span>Enter a nominal size and tolerance designation.</span>
      </header>

      <section className="limitCalculator">
        <label htmlFor="limit-designation">
          Tolerance designation
        </label>
        <input
          id="limit-designation"
          className="limitDesignationInput"
          type="text"
          autoComplete="off"
          spellCheck="false"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          placeholder="Enter e.g. 25H7"
          aria-describedby={message ? 'limit-input-message' : undefined}
        />

        <div className="limitTypeControl" aria-label="Tolerance type">
          {['hole', 'shaft'].map((type) => (
            <button
              key={type}
              type="button"
              className={selectedType === type ? 'isSelected' : ''}
              aria-pressed={selectedType === type}
              onClick={() => handleTypeChange(type)}
            >
              {type === 'hole' ? 'Hole' : 'Shaft'}
            </button>
          ))}
        </div>
        <p className="limitTypeHelp">
          Choose whether the tolerance applies to a hole or shaft.
        </p>
        <p className="limitTypeHelp">The number in a designation such as 25H7 is always millimetres.</p>

        {message && (
          <p
            id="limit-input-message"
            className={`limitMessage limitMessage--${message.type}`}
            role={message.type === 'error' ? 'alert' : 'status'}
          >
            {message.text}
          </p>
        )}
      </section>

      <section className="limitResultArea" aria-live="polite" aria-busy={loading}>
        {loading && (
          <p className="limitLoading"><span aria-hidden="true" />Loading tolerance…</p>
        )}

        {result && (
          <article className="limitResultCard">
            <div className="limitResultHeading">
              <span className="limitResultIcon" aria-hidden="true">
                <Ruler size={22} strokeWidth={1.9} />
              </span>
              <div>
                <p>{formatDesignation(result, precision)}</p>
                <span>ISO 286 · {result.type === 'hole' ? 'Hole' : 'Shaft'}</span>
              </div>
            </div>

            <div className="limitPrimaryResult">
              <span>Dimensional limits</span>
              <strong>
                {formatDimension(result.limits.minimumMm, precision, unitSystem)} –{' '}
                {formatDimension(result.limits.maximumMm, precision, unitSystem)} {unitLabel('mm', unitSystem)}
              </strong>
            </div>

            <dl className="limitSupportingValues">
              <div><dt>Lower deviation</dt><dd>{formatDeviation(result.deviations.lowerUm, unitSystem)}</dd></div>
              <div><dt>Upper deviation</dt><dd>{formatDeviation(result.deviations.upperUm, unitSystem)}</dd></div>
              <div><dt>Tolerance width</dt><dd>{formatUnitValue(result.deviations.upperUm - result.deviations.lowerUm, 'um', unitSystem)} {unitLabel('um', unitSystem)}</dd></div>
            </dl>

            <ToleranceField
              lowerUm={result.deviations.lowerUm}
              upperUm={result.deviations.upperUm}
              unitSystem={unitSystem}
            />

            <div className="limitFitsAction">
              <Link to={fitsPath}>Use in Fits</Link>
              {gaugePath && <Link to={gaugePath}>Check Gauge</Link>}
            </div>

            {hasAdjacentResults && (
              <div className="limitAdjacent">
                <button
                  type="button"
                  className="limitAdjacentToggle"
                  aria-expanded={adjacentOpen}
                  aria-controls="limit-adjacent-list"
                  onClick={() => setAdjacentOpen((open) => !open)}
                >
                  Compare adjacent grades
                  <ChevronDown size={20} aria-hidden="true" />
                </button>

                {adjacentOpen && (
                  <ul id="limit-adjacent-list">
                    <CompactTolerance result={adjacentResults.lowerGrade} label="tighter" unitSystem={unitSystem} />
                    <CompactTolerance result={result} label="selected" selected unitSystem={unitSystem} />
                    <CompactTolerance result={adjacentResults.higherGrade} label="looser" unitSystem={unitSystem} />
                  </ul>
                )}
              </div>
            )}
          </article>
        )}
      </section>

      <div className="limitSeoContent">
        <section aria-labelledby="iso-limit-deviations-heading">
          <h2 id="iso-limit-deviations-heading">ISO 286 limit deviations</h2>
          <p>
            ISO 286 defines tolerance zones for holes and shafts relative to a
            nominal size. This calculator uses verified tolerance records to
            show the permitted dimensional range for an available designation.
          </p>
        </section>

        <section aria-labelledby="read-designation-heading">
          <h2 id="read-designation-heading">How to read 25H7</h2>
          <p>
            In <strong>25H7</strong>, 25 is the nominal size in millimetres, H
            is the hole tolerance position and 7 is the IT grade. Uppercase
            positions identify holes; lowercase positions identify shafts, as
            in <strong>25h7</strong>.
          </p>
        </section>

        <section aria-labelledby="understand-result-heading">
          <h2 id="understand-result-heading">Understanding the result</h2>
          <p>The result gives the permitted size range and its supporting values:</p>
          <ul>
            <li>minimum and maximum permissible size,</li>
            <li>lower and upper deviation from the nominal size,</li>
            <li>and the total tolerance width.</li>
          </ul>
          <p>
            Hole deviations are designated EI (lower) and ES (upper). Shaft
            deviations use ei (lower) and es (upper).
          </p>
        </section>

        <section aria-labelledby="supported-range-heading">
          <h2 id="supported-range-heading">Supported range</h2>
          <p>
            ASiJoLab uses a practical, verified ISO 286 dataset for nominal
            sizes from 1 to 500 mm. Available positions and IT grades depend on
            the verified records present in that dataset. Unsupported
            combinations are intentionally not estimated or generated.
          </p>
        </section>
      </div>
    </main>
  );
};

export default LimitDeviationsPage;
